import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';

import { FileSpecificationService } from 'src/app/services/file-specifications/file-specification.service';
import { FilenameConventionService } from 'src/app/services/filename-conventions/filename-convention.service';
import { OutputFormulaService } from 'src/app/services/output-formulas/output-formula.service';

export type OutputCompositionEntityKind =
  | 'file-specification'
  | 'filename-convention'
  | 'output-formula';

export interface OutputCompositionHistoryDialogData {
  organizationId: string;
  entityKind: OutputCompositionEntityKind;
  id: string;
  name: string;
  media?: 'stills' | 'videos';
  mode?: 'rangeBased' | 'evBased';
}

export type WriteReasonKind =
  | 'initialCreate'
  | 'preferenceUpdate'
  | 'correction'
  | 'deleted'
  | 'other';

export type ReasonCssClass =
  | 'initial-create'
  | 'preference-update'
  | 'correction'
  | 'deleted'
  | 'other';

export interface RevisionRow {
  id: string;
  createdAt: string;
  name: string;
  reasonKind: WriteReasonKind;
  reasonLabel: string;
  reasonClass: ReasonCssClass;
  reasonDetail?: string;
  summary: string;
  isLatest: boolean;
}

interface AuditReason {
  kind: WriteReasonKind;
  detail?: string;
}

interface AuditableRevision {
  id: string;
  createdAt: string;
  name: string;
  writeReason: AuditReason;
}

const ENTITY_TITLES: Record<OutputCompositionEntityKind, string> = {
  'file-specification': 'File specification history',
  'filename-convention': 'Filename convention history',
  'output-formula': 'Output formula history',
};

const REASON_LABELS: Record<WriteReasonKind, string> = {
  initialCreate: 'Initial create',
  preferenceUpdate: 'Preference update',
  correction: 'Correction',
  deleted: 'Deleted',
  other: 'Other',
};

const REASON_CLASSES: Record<WriteReasonKind, ReasonCssClass> = {
  initialCreate: 'initial-create',
  preferenceUpdate: 'preference-update',
  correction: 'correction',
  deleted: 'deleted',
  other: 'other',
};

@Component({
  selector: 'app-output-composition-history-dialog',
  templateUrl: './output-composition-history-dialog.component.html',
  styleUrls: ['./output-composition-history-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OutputCompositionHistoryDialogComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  loaded = false;
  revisions: RevisionRow[] = [];

  private readonly expandedIds = new Set<string>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OutputCompositionHistoryDialogData,
    private readonly dialogRef: MatDialogRef<OutputCompositionHistoryDialogComponent, void>,
    private readonly fileSpecificationService: FileSpecificationService,
    private readonly filenameConventionService: FilenameConventionService,
    private readonly outputFormulaService: OutputFormulaService,
  ) {
    this.loadAudit();
  }

  get title(): string {
    return ENTITY_TITLES[this.data.entityKind];
  }

  get revisionsCount(): number {
    return this.revisions.length;
  }

  isExpanded(revision: RevisionRow): boolean {
    return this.expandedIds.has(revision.id);
  }

  toggle(revision: RevisionRow): void {
    if (this.expandedIds.has(revision.id)) {
      this.expandedIds.delete(revision.id);
    } else {
      this.expandedIds.add(revision.id);
    }
    this.cdr.markForCheck();
  }

  close(): void {
    this.dialogRef.close();
  }

  private loadAudit(): void {
    const source$: Observable<AuditableRevision[]> = this.pickSource();
    source$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (rows: AuditableRevision[]) => this.applyRevisions(rows),
      error: () => {
        this.loaded = true;
        this.cdr.markForCheck();
      },
    });
  }

  private applyRevisions(rows: AuditableRevision[]): void {
    const sorted = this.sortLatestFirst(rows);
    this.revisions = sorted.map((row, index) => this.toRow(row, index === 0));
    this.expandedIds.clear();
    if (this.revisions.length > 0) this.expandedIds.add(this.revisions[0].id);
    this.loaded = true;
    this.cdr.markForCheck();
  }

  private sortLatestFirst(rows: AuditableRevision[]): AuditableRevision[] {
    return [...rows].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  private toRow(row: AuditableRevision, isLatest: boolean): RevisionRow {
    const reasonKind = row.writeReason.kind;
    return {
      id: row.id,
      createdAt: row.createdAt,
      name: row.name,
      reasonKind,
      reasonLabel: REASON_LABELS[reasonKind],
      reasonClass: REASON_CLASSES[reasonKind],
      reasonDetail: reasonKind === 'other' ? row.writeReason.detail : undefined,
      summary: this.summarise(row.writeReason),
      isLatest,
    };
  }

  private summarise(reason: AuditReason): string {
    if (reason.kind === 'initialCreate') return 'First save · initial revision.';
    if (reason.kind === 'preferenceUpdate') return 'Preference update — settings adjusted.';
    if (reason.kind === 'correction') return 'Correction — fix applied to a previous revision.';
    if (reason.kind === 'deleted') return 'Soft deleted · no longer available for new collections.';
    return reason.detail !== undefined && reason.detail !== ''
      ? `Other — ${reason.detail}`
      : 'Other — no detail supplied.';
  }

  private pickSource(): Observable<AuditableRevision[]> {
    if (this.data.entityKind === 'file-specification') {
      return this.fileSpecificationService.getAudit(
        this.data.organizationId,
        this.data.id,
        this.data.media,
      ) as Observable<AuditableRevision[]>;
    }
    if (this.data.entityKind === 'filename-convention') {
      return this.filenameConventionService.getAudit(
        this.data.organizationId,
        this.data.id,
        this.data.mode,
      ) as Observable<AuditableRevision[]>;
    }
    return this.outputFormulaService.getAudit(
      this.data.organizationId,
      this.data.id,
    ) as Observable<AuditableRevision[]>;
  }
}
